import {GRANITE_9} from '@gaia/colors';
import Button from '@gaia/components/Button';
import useActivities from '@gaia/hooks/useActivities';
import {useLatest} from '@gaia/hooks/useLatest';
import {
    biking,
    boating,
    fishing,
    hiking,
    hunting,
    offroad,
    other,
    professional,
    snow,
} from '@gaia/icons/activities';
import theme from '@gaia/theme';
import type {ActivityCategoryId, ActivityData} from '@gaia/types';
import {Divider, List, ListItem, ListItemText, Typography} from '@mui/material';
import isEqual from 'lodash/isEqual';
import memoize from 'lodash/memoize';
import sortBy from 'lodash/sortBy';
import {useCallback, useEffect} from 'react';

export interface ActivityListProps {
    selectedActivities: string[];
    setSelectedActivities: SetState<string[]>;
    allowMultiSelections: boolean;
    onSelection?: (activityId: string) => void;
}

export const getUpdatedActivityIds = memoize(
    (selectedActivities: string[], activities: ActivityData[] | undefined): string[] => {
        //check if activity is an old id and return list with updated ids
        const activityIds: string[] = [];
        selectedActivities.forEach((item) => {
            const activityObj = activities?.find(
                (activity) =>
                    activity.id === item.toLocaleLowerCase() ||
                    activity.old_ids.includes(item.toLocaleLowerCase())
            );
            if (activityObj) {
                activityIds.push(activityObj.id);
            }
        });
        return activityIds;
    },
    (...args) => JSON.stringify(args)
);

export default function ActivityList({
    allowMultiSelections,
    selectedActivities,
    setSelectedActivities,
    onSelection,
}: ActivityListProps) {
    const [activitiesData] = useActivities();
    const categories = activitiesData?.filter((activity) => activity.category);

    const latest = useLatest({selectedActivities, setSelectedActivities});

    useEffect(() => {
        // update users selected activities ids' if they are old
        if (activitiesData) {
            const updateActivities = getUpdatedActivityIds(
                latest.current.selectedActivities,
                activitiesData
            );
            if (!isEqual(sortBy(updateActivities), sortBy(latest.current.selectedActivities)))
                latest.current.setSelectedActivities(updateActivities);
        }
    }, [latest, activitiesData]);

    const onSelectActivity = useCallback(
        (activityId: string) => {
            if (allowMultiSelections) {
                const removeItem = selectedActivities.find((activity) => activity === activityId);
                if (removeItem) {
                    setSelectedActivities(
                        selectedActivities.filter((activity) => activity !== activityId)
                    );
                } else {
                    setSelectedActivities(selectedActivities.concat(activityId));
                }
            } else {
                setSelectedActivities([activityId]);
            }
            if (onSelection) {
                onSelection(activityId);
            }
        },
        [allowMultiSelections, setSelectedActivities, selectedActivities, onSelection]
    );

    if (!activitiesData) return null;

    return (
        <List
            style={{
                overflow: 'scroll',
                height: 315,
            }}
        >
            {categories?.map((category) => (
                <Categories
                    key={category.id}
                    activities={activitiesData}
                    category={category}
                    selectedActivities={selectedActivities}
                    onSelectActivity={onSelectActivity}
                />
            ))}
        </List>
    );
}

export const getCategoryImage = (categoryId: ActivityCategoryId) => {
    // return image and alt tag string based on category id. Default to 'other'.
    const catImgs = {
        hiking,
        offroad,
        snow,
        biking,
        hunting,
        professional,
        fishing,
        boating,
        other,
    };

    return catImgs[categoryId] || catImgs['other'];
};

function Categories({
    activities,
    category,
    selectedActivities,
    onSelectActivity,
}: {
    activities: ActivityData[];
    category: ActivityData;
    selectedActivities: string[];
    onSelectActivity: (activityId: string) => void;
}) {
    const categoryActivities = activities.filter((activity) => activity.id.startsWith(category.id));

    const catImg = getCategoryImage(category.id as ActivityCategoryId);
    return (
        <>
            <ListItem key={category.id} dense disableGutters>
                <ListItemText
                    disableTypography
                    id={category.id}
                    primary={
                        <div style={{display: 'flex', alignItems: 'center'}}>
                            <img
                                src={catImg}
                                style={{
                                    width: 30,
                                    height: 30,
                                    margin: theme.spacing(1),
                                }}
                                alt=''
                            />
                            {/* component used on map and profile which have 2 different base fonts so the font size needs to be hard coded */}
                            <Typography variant='h5' style={{fontSize: 18}}>
                                {category.display_name}
                            </Typography>
                        </div>
                    }
                    secondary={
                        <>
                            <Divider style={{width: '100%'}} />
                            <div
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    backgroundColor: GRANITE_9,
                                }}
                            >
                                {sortBy(categoryActivities, 'id').map((activity) => (
                                    <Button
                                        key={activity.id}
                                        size='small'
                                        appearance={
                                            selectedActivities.includes(activity.id)
                                                ? 'primary'
                                                : 'outlined'
                                        }
                                        style={{
                                            margin: theme.spacing(1),
                                        }}
                                        onClick={() => {
                                            onSelectActivity(activity.id);
                                        }}
                                    >
                                        {activity.display_name}
                                    </Button>
                                ))}
                            </div>
                        </>
                    }
                />
            </ListItem>
        </>
    );
}
